import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  fetchCategories,
  createCategory,
  deleteCategories,
  editCategories,
  toggleCategorySelection,
  toggleAllSelection,
  clearSelected
} from '../../store/Blog/categoriesSlice'
import Swal from 'sweetalert2';
import 'bootstrap/dist/css/bootstrap.min.css';
import './style.css';

const CategoriesManagement = () => {
  const dispatch = useDispatch();
  const { categories, flatCategories, loading, error } = useSelector(state => state.blogCategories);
  
  // Form states
  const [categoryName, setCategoryName] = useState('');
  const [categorySlug, setCategorySlug] = useState('');
  const [parentCategory, setParentCategory] = useState('');
  const [categoryDescription, setCategoryDescription] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [bulkAction, setBulkAction] = useState('-- Select --');

  // Fetch categories on component mount
  useEffect(() => {
    dispatch(fetchCategories());
  }, [dispatch]);

  // Handle adding a new category
  const handleAddCategory = async () => {
    if (!categoryName.trim()) {
      Swal.fire('Error', 'Category name is required', 'error');
      return;
    }

    try {
      await dispatch(createCategory({
        name: categoryName,
        slug: categorySlug,
        description: categoryDescription,
        parent: parentCategory || null
      })).unwrap();

      Swal.fire('Success', 'Category added successfully', 'success');
      setCategoryName('');
      setCategorySlug('');
      setCategoryDescription('');
      setParentCategory('');
    } catch (err) {
      Swal.fire('Error', err.message || 'Failed to add category', 'error');
    }
  };

  // Handle search
  const handleSearch = () => {
    // Search functionality would be implemented here
    console.log('Searching for:', searchTerm);
  };

  // Handle bulk actions
  const handleBulkAction = async () => {
    if (bulkAction === '-- Select --') {
      Swal.fire('Warning', 'Please select a bulk action', 'warning');
      return;
    }

    const selectedCategories = flatCategories.filter(cat => cat.selected);
    if (selectedCategories.length === 0) {
      Swal.fire('Warning', 'No categories selected', 'warning');
      return;
    }

    const selectedIds = selectedCategories.map(cat => cat._id);

    if (bulkAction === 'Delete') {
      const result = await Swal.fire({
        title: 'Are you sure?',
        text: `You are about to delete ${selectedCategories.length} categories. This cannot be undone!`,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Delete',
        cancelButtonText: 'Cancel'
      });

      if (result.isConfirmed) {
        try {
          await dispatch(deleteCategories(selectedIds)).unwrap();
          Swal.fire('Deleted!', `${selectedCategories.length} categories deleted.`, 'success');
          dispatch(clearSelected());
        } catch (err) {
          Swal.fire('Error', err.message || 'Failed to delete categories', 'error');
        }
      }
    } else if (bulkAction === 'Edit') {
      const { value: newName } = await Swal.fire({
        title: 'Update Categories',
        input: 'text',
        inputLabel: 'New name for selected categories',
        inputPlaceholder: 'Enter new name',
        showCancelButton: true,
        inputValidator: (value) => {
          if (!value) return 'You need to enter a name!';
        }
      });

      if (newName) {
        try {
          await dispatch(editCategories({
            ids: selectedIds,
            name: newName
          })).unwrap();
          
          Swal.fire('Updated!', `${selectedCategories.length} categories updated.`, 'success');
          dispatch(clearSelected());
        } catch (err) {
          Swal.fire('Error', err.message || 'Failed to update categories', 'error');
        }
      }
    }
  };

  // Toggle category selection
  const handleToggleCategorySelection = (id) => {
    dispatch(toggleCategorySelection(id));
  };

  // Toggle all categories selection
  const handleToggleAllCategoriesSelection = (e) => {
    dispatch(toggleAllSelection(e.target.checked));
  };

  // Get parent categories for dropdown
  const parentCategories = categories.filter(category => !category.parent);

  // Filter categories based on search term
  const filteredCategories = flatCategories.filter(category =>
    category.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) return <div className="text-center py-5">Loading categories...</div>;
  if (error) return <div className="alert alert-danger">Error: {error}</div>;

  return (
    <div className="product-wrap col-md-10 mx-auto mt-3 p-3" style={{background: '#F6F6F6'}}>
      <div className="Container">
        <div className="row mb-3">
          <div className="col-md-6">
            <h2>Blog Categories</h2> 
          </div>
          <div className="col-md-6">
            <div className="header-rgt">
              <input 
                type="text" 
                className="form-control wdt-40" 
                placeholder="Search categories..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />  
              <button className="btn-cls" onClick={handleSearch}>
                Search Categories
              </button>
            </div> 
          </div>
        </div>

        <div className="row">
          <div className="col-md-4 add-category">
            <h4>Add New Category</h4>

            <div className="form-group mb-3">
              <label>Name</label>
              <input 
                type="text" 
                className="form-control" 
                value={categoryName}
                onChange={(e) => setCategoryName(e.target.value)}
                placeholder="Category name"
              />
              <small className="form-text text-muted">
                The name is how it appears on your site.
              </small>
            </div>
            
            <div className="form-group mb-3">
              <label>Slug</label>
              <input 
                type="text" 
                className="form-control" 
                value={categorySlug}
                onChange={(e) => setCategorySlug(e.target.value)}
                placeholder="Category slug (optional)"
              />
              <small className="form-text text-muted">
                The "slug" is the URL-friendly version of the name. It is usually all lowercase
                and contains only letters, numbers, and hyphens.
              </small>
            </div>
            
            <div className="form-group mb-3">
              <label>Parent Category</label>
              <select 
                className="form-control form-select w-50"
                value={parentCategory}
                onChange={(e) => setParentCategory(e.target.value)}
              > 
                <option value="">-- None --</option>
                {parentCategories.map(cat => (
                  <option key={cat._id} value={cat._id}>{cat.name}</option>
                ))}
              </select>
              <small className="form-text text-muted">
                Categories, unlike tags, can have a hierarchy. You might have a Jazz
                category, and under that have children categories for Bebop and Big Band.
                Totally optional.
              </small>
            </div>
            
            <div className="form-group mb-3">
              <label>Description</label>
              <input 
                type="text" 
                className="form-control" 
                value={categoryDescription}
                onChange={(e) => setCategoryDescription(e.target.value)}
                placeholder="Description (optional)"
              />
              <small className="form-text text-muted custom-txt">
                The description is not prominent by default; however, some themes may show it.
              </small>
            </div>
            
            <button className="btn-cls" onClick={handleAddCategory}>
              Add New Category
            </button>
          </div>

          <div className="col-md-1"></div>

          <div className="col-md-7">
            <div className="blog-inner tab-brdr">
              <div className="table-header">
                <div className="filter">
                  <div className="w-100">
                    <div className="row">
                      <div className="col-md-8">
                        <div className="frm-outer">
                          <select 
                            className="form-control form-select"
                            value={bulkAction}
                            onChange={(e) => setBulkAction(e.target.value)}
                          > 
                            <option value="-- Select --">Bulk Action</option>
                            <option value="Delete">Delete</option>
                            <option value="Edit">Edit</option>
                          </select>
                          
                          <button className="btn-cls" onClick={handleBulkAction}>
                            Apply
                          </button>	
                        </div> 
                      </div>
                     
                      <div className="col-md-4 right-align">
                        <p style={{textAlign: 'right'}}>{flatCategories.length} Items</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <table className="table table-striped m-0">
                <thead>
                  <tr>
                    <th>
                      <input 
                        type="checkbox" 
                        className="form-check-input" 
                        onChange={handleToggleAllCategoriesSelection}
                        checked={flatCategories.length > 0 && flatCategories.every(cat => cat.selected)}
                      />
                    </th>
                    <th>Name</th>
                    <th>Description</th>
                    <th>Slug</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredCategories.map(category => (
                    <tr key={category._id}>
                      <td>
                        <input 
                          type="checkbox" 
                          className="form-check-input" 
                          checked={category.selected}
                          onChange={() => handleToggleCategorySelection(category._id)}
                        />
                      </td>
                      <td>
                        {category.level > 0 && (
                          <span style={{ marginLeft: `${category.level * 20}px` }}>↳ </span>
                        )}
                        {category.name}
                      </td>
                      <td>{category.description || '—'}</td>
                      <td>{category.slug}</td>
                      <td>{category.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CategoriesManagement;